import { useCallback, useMemo } from 'react';
import { useGetRecommendedCatalogCategoriesQuery } from '@graphql/generated';
import type { RawCatalogEntityCategory, RawCatalogCategory, RecommendedCatalogCategory } from './RecommendedCategoriesSection.types';
import { useCatalogRouting } from '../../../../useCatalogRouting';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { useCatalogLoggingContext } from '../../../../Catalog.logging';

type UseRecommendedCategoriesSectionArgs = {
  eventId: Maybe<string>;
  skip: boolean;
};

const isValidCategoryObject = (val: RawCatalogCategory): val is RecommendedCatalogCategory => {
  return !!val?.title;
};

export const useRecommendedCategoriesSection = ({ eventId, skip }: UseRecommendedCategoriesSectionArgs) => {
  const { currentStorefront } = useCatalogRoutePaths();
  const { data, loading, error } = useGetRecommendedCatalogCategoriesQuery({
    variables: { eventId, storefront: currentStorefront },
    batchMode: 'fast',
    skip,
  });

  const { trackCategoryNavigateAction } = useCatalogLoggingContext();

  const { goToCategory } = useCatalogRouting();

  const catalogEntityCategories = useMemo<RawCatalogEntityCategory[]>(() => {
    const catalogEntities = data?.recommendCatalogHomeSections.sections[0].catalogEntities ?? [];

    return catalogEntities.reduce((acc, entity) => {
      if (entity.__typename === 'ProductCatalogEntityCategory' && isValidCategoryObject(entity.catalogCategoryData) && !!entity.catalogCategoryData.isFeatured) {
        const subcategoriesWithContent = entity.catalogCategoryData.subCategories.filter(subCat => subCat.directItemCount > 0);

        if (subcategoriesWithContent.length > 0) {
          acc.push({
            ...entity,
            catalogCategoryData: {
              ...entity.catalogCategoryData,
              subCategories: subcategoriesWithContent,
            },
          });
        }
      }
      return acc;
    }, [] as Array<RawCatalogEntityCategory>);

    // return filteredCatalogEntities;
  }, [data]);

  const goToCatMapped = useCallback(
    (category: { id: string; title: string; publicUrlPath: string }, navigationSource: 'categoryRecShowAll' | 'categoryRecBrowseMore' | 'categoryShowcase') => {
      goToCategory(category.publicUrlPath);
      trackCategoryNavigateAction({ category, navigationSource });
    },
    [goToCategory, trackCategoryNavigateAction],
  );

  return {
    catalogEntityCategories,
    loadingCategorySections: loading,
    errorCategorySections: error,
    goToCategory: goToCatMapped,
  };
};
